import { useEffect, useMemo, useRef, useState } from 'react';
import { Appointment } from '@weave/schema-gen-ts/dist/schemas/schedule/calendar-events/v1/calendar_events.pb';
import {
  EventLocationType,
  EventStatus,
  EventType,
} from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import dayjs from 'dayjs';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { formatDate } from '@frontend/date';
import { formatDateTime, isValidDate } from '@frontend/schedule-helpers';
import { DATETIME_FORMATS, DEFAULT_APPOINTMENT_STATUS } from '../../../../constants';
import { useCalendarEvent } from '../../../../context/CalendarEventProvider';
import { useCalendarEventsConfigurationShallowStore, useSchedulingLocationInfo } from '../../../../hooks';
import { useOnboardingFlowInfo } from '../../../../hooks/onboarding-flow-hooks';
import { transformDateSlotsToOpeningBlocks, transformV3ResponsesForReviewRequest } from '../../../../utils';
import { getDateByTimezone, isSameOrAfterDate, isTodayDate } from '../../../../utils/date-helpers';
import { dispatchFocusAppointmentEvent } from '../../../CalendarV3/use-calendar-custom-events';
import { CalendarEventsEnums } from '../../types';
import { normalizeString } from '../../utils';
import { AppointmentTypeExtended, NewAppointmentForm } from './NewAppointmentForm';
import { AppointmentAvailabilityFormValues, OnCreateCalendarEvent, OnUpdateCalendarEvent } from './types';

type NewAppointmentFormContainerV3Props = {
  eventType: CalendarEventsEnums;
  closeModal: () => void;
  onAppointmentSuccess?: (appointment?: Appointment) => void;
};

const getStartEndDatesForAppointment = (startDate: string, duration: number) => {
  const startDateObj = dayjs(startDate);
  const endDateObj = startDateObj.add(duration, 'minute');

  return {
    startDate: startDateObj.format('YYYY-MM-DD'),
    startTime: startDateObj.format('HH:mm:ss'),
    endDate: endDateObj.format('YYYY-MM-DD'),
    endTime: endDateObj.format('HH:mm:ss'),
  };
};

export const NewAppointmentFormContainerV3 = ({
  closeModal,
  onAppointmentSuccess,
}: NewAppointmentFormContainerV3Props) => {
  const { selectedLocationIds, providersList } = useCalendarEvent();
  const { getLocationTimeZone, getScopeName } = useSchedulingLocationInfo();
  const { setShowOnNextLogin, setOnboardingFlowInfo, isBookAppointmentVisible } = useOnboardingFlowInfo();
  const isApptCreateSuccessRef = useRef(false);

  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const locationTimezone = getLocationTimeZone(selectedLocationId);

  const [selectedFormValuesForAvailabilityApi, setSelectedFormValuesForAvailabilityApi] =
    useState<AppointmentAvailabilityFormValues>({} as AppointmentAvailabilityFormValues);

  const { configuredCalendarEvent } = useCalendarEventsConfigurationShallowStore('configuredCalendarEvent');

  const { data: appointmentTypesV3, refetch: refetchAppointmentTypes } =
    SchedulerV3.Helpers.useGetAppointmentTypesV3Data({
      selectedLocationId: selectedLocationIds[0],
      selectedLocationIds: selectedLocationIds,
      pageConfig: { limit: SchedulerV3.Constants.CALENDAR_VIEW_V3_API_QUERY_LIMIT, page: 1 },
    });

  const { isIntegratedOffice } = DataSourcesQueries.useGetAccessibleIntegrations();

  useEffect(() => {
    return () => {
      if (!isApptCreateSuccessRef.current && isBookAppointmentVisible) {
        // set show onboarding flow info on next login if appointment is not created successfully
        setShowOnNextLogin();
      }
    };
  }, []);

  const appointmentTypesData = useMemo(() => {
    return transformV3ResponsesForReviewRequest({
      // Filtered appointment types should have all unarchived appointment types and the selected appointment (in case of edit) even if if is an archived appointment
      // `active` property does not exist in v2, so it won't affect any of the v2 appointment types
      appointmentTypesDataV3: appointmentTypesV3?.appointmentTypes.filter((apptType) => {
        if (apptType.locationId !== selectedLocationId) return false;

        const isSelectedApptType =
          normalizeString(apptType.displayName || apptType.externalName || '') ===
          normalizeString(configuredCalendarEvent?.appointmentType);

        return isSelectedApptType || apptType.details?.act;
      }),
    }).transformedAppointmentTypes as AppointmentTypeExtended[];
  }, [appointmentTypesV3?.appointmentTypes, configuredCalendarEvent?.appointmentType, selectedLocationId]);

  const filteredProviderList = useMemo(
    () =>
      // Filtered providers should have all active providers and the selected provider (in case of edit)
      // even if it is an archived provider, but not the hidden (Show on Calendar = false)
      // `isActive` property exists in v2 as well, but it won't affect list as we only get active providers from API in v2
      providersList?.filter(
        (provider) => provider.id === configuredCalendarEvent?.providerId || (!provider.hidden && provider.isActive)
      ) ?? [],
    [configuredCalendarEvent?.providerId, providersList]
  );

  const createCalendarEventMutation = SchedulerV3.Mutations.useCreateCalendarEventMutation();
  const updateCalendarEventMutation = SchedulerV3.Mutations.useUpdateCalendarEventMutation();

  const availabilityApiParams = useMemo(() => {
    if (selectedFormValuesForAvailabilityApi.appointmentDate) {
      const selectedDate = getDateByTimezone(
        selectedFormValuesForAvailabilityApi.appointmentDate,
        DATETIME_FORMATS.US_DATE,
        locationTimezone
      );
      const startDateTime = selectedDate.startOf('day').format(DATETIME_FORMATS.ISO_DATETIME_WITH_OFFSET);
      const endDateTime = selectedDate.endOf('day').format(DATETIME_FORMATS.ISO_DATETIME_WITH_OFFSET);

      return {
        startDate: startDateTime,
        endDate: endDateTime,
      };
    }
    return {
      startDate: '',
      endDate: '',
    };
  }, [locationTimezone, selectedFormValuesForAvailabilityApi.appointmentDate]);

  const isFutureDateSelected =
    isValidDate(dayjs(selectedFormValuesForAvailabilityApi.appointmentDate)) &&
    isSameOrAfterDate(dayjs(selectedFormValuesForAvailabilityApi.appointmentDate), dayjs().startOf('day'));

  const listCalendarAvailabilitiesQuery = SchedulerV3.Queries.useListCalendarAvailabilitiesQuery({
    request: {
      locationId: selectedLocationId,
      calendarId: selectedFormValuesForAvailabilityApi.selectedPractitionerCalendarId || '',
      startDateTime: availabilityApiParams.startDate || '',
      endDateTime: availabilityApiParams.endDate || '',
      durationMinutes: selectedFormValuesForAvailabilityApi.duration,
      clientTimezone: dayjs.tz.guess(),
      appointmentTypeId: selectedFormValuesForAvailabilityApi?.appointmentType ?? '',
    },
    options: {
      retry: 1,
      enabled:
        !!selectedLocationId &&
        !!selectedFormValuesForAvailabilityApi.selectedPractitionerCalendarId &&
        !!availabilityApiParams.startDate &&
        !!availabilityApiParams.endDate &&
        !!selectedFormValuesForAvailabilityApi.duration &&
        isFutureDateSelected,
    },
  });

  const availableAppointmentTimes = useMemo(() => {
    const isTodaySelected = isTodayDate(dayjs(selectedFormValuesForAvailabilityApi.appointmentDate));

    if (!listCalendarAvailabilitiesQuery.data?.availableSlots?.length) return [];

    return transformDateSlotsToOpeningBlocks(
      listCalendarAvailabilitiesQuery.data.availableSlots,
      isTodaySelected,
      locationTimezone
    );
  }, [
    listCalendarAvailabilitiesQuery?.data?.availableSlots,
    selectedFormValuesForAvailabilityApi.appointmentDate,
    locationTimezone,
  ]);

  const onUpdateAppointment = async (
    {
      personId,
      startDate: appointmentDateTime,
      practitionerId,
      duration,
      id,
      practitionerCalendarId,
      referenceId,
      practitionerName,
      appointmentTypeName,
      appointmentTypeId,
      locationId,
      backgroundColor,
    }: OnUpdateCalendarEvent,
    status?: EventStatus
  ) => {
    const { startDate, startTime, endDate, endTime } = getStartEndDatesForAppointment(appointmentDateTime, duration);

    const locationTimezone = getLocationTimeZone(locationId);

    const updatedCalendarEvent = await updateCalendarEventMutation.mutateAsync({
      attendeeId: personId,
      attendeeStatus: status || DEFAULT_APPOINTMENT_STATUS,
      eventType: EventType.APPOINTMENT,
      locationType: EventLocationType.OFFICE,
      organizerId: practitionerId,
      organizerCalendarId: practitionerCalendarId || '',
      startDate,
      startTime,
      endDate,
      endTime,
      startTimezone: locationTimezone,
      endTimezone: locationTimezone,
      eventId: id,
      referenceId: referenceId || '',
      referenceTypeId: appointmentTypeId || '',
      details: {
        dur: duration,
        orNa: practitionerName,
        rTyNa: appointmentTypeName,
        col: backgroundColor || '',
      },
      existingStartDate: formatDate(configuredCalendarEvent?.startDate, DATETIME_FORMATS.ISO_DATE) || '',
    });

    onAppointmentSuccess?.({
      clientLocationIds: updatedCalendarEvent?.event?.sourceTenantId
        ? [updatedCalendarEvent?.event?.sourceTenantId]
        : [],
      clientLocationId: updatedCalendarEvent?.event?.sourceTenantId || '',
      id: updatedCalendarEvent?.event?.id || '',
      locationId: updatedCalendarEvent?.event?.locationId || '',
    });
  };

  const onCreateAppointment = async (
    {
      personId,
      startDate: appointmentDateTime,
      practitionerId,
      duration,
      practitionerCalendarId,
      locationId,
      sourceId,
      appointmentTypeId,
      practitionerName,
      appointmentTypeName,
      backgroundColor,
    }: OnCreateCalendarEvent,
    status?: EventStatus
  ) => {
    const { startDate, startTime, endDate, endTime } = getStartEndDatesForAppointment(appointmentDateTime, duration);

    const locationTimezone = getLocationTimeZone(locationId);
    const appointmentResponse = await createCalendarEventMutation.mutateAsync({
      attendeeId: personId,
      attendeeStatus: status || DEFAULT_APPOINTMENT_STATUS,
      eventType: EventType.APPOINTMENT,
      locationType: EventLocationType.OFFICE,
      organizerId: practitionerId,
      organizerCalendarId: practitionerCalendarId || '',
      startDate,
      startTime,
      endDate,
      endTime,
      startTimezone: locationTimezone,
      endTimezone: locationTimezone,
      locationId,
      referenceTypeId: appointmentTypeId || '',
      details: {
        dur: duration,
        orNa: practitionerName,
        rTyNa: appointmentTypeName,
        col: backgroundColor || '',
      },
      sourceTenantId: sourceId || locationId, // Using sourceId or locationId as sourceTenantId because non integrated offices do not have sourceTenantId
    });

    SchedulerV3.trackPendoEvent('appointment_created', {
      entryPoint: 'calendar',
      createdByUserType: 'provider',
      appointmentId: appointmentResponse?.event?.id || '',
      appointmentProviderId: practitionerId,
      appointmentType: appointmentTypeName ?? '',
      appointmentDate: startDate,
      appointmentStartTime: startTime,
      appointmentEndTime: endTime,
      clientId: personId,
      locationId: locationId,
      locationName: getScopeName(locationId),
    });

    if (isBookAppointmentVisible) {
      isApptCreateSuccessRef.current = true;
      setOnboardingFlowInfo({ status: 'completed_appointment_creation' });
      dispatchFocusAppointmentEvent({
        appointmentId: appointmentResponse?.event?.id || '',
        calendarDate: formatDateTime(appointmentDateTime, {
          fromTimezone: 'UTC',
          toTimezone: locationTimezone,
          outputFormat: 'MM/DD/YYYY',
        }),
        locationId: locationId,
      });
    }

    onAppointmentSuccess?.({
      clientLocationIds: appointmentResponse?.event?.sourceTenantId ? [appointmentResponse?.event?.sourceTenantId] : [],
      clientLocationId: appointmentResponse?.event?.sourceTenantId || '',
      id: appointmentResponse?.event?.id || '',
      locationId: appointmentResponse?.event?.locationId || '',
    });
  };

  return (
    <NewAppointmentForm
      availableAppointmentTimes={availableAppointmentTimes ?? []}
      closeModal={closeModal}
      isAvailableAppointmentTimesLoading={listCalendarAvailabilitiesQuery.isLoading}
      isScheduleV3
      isStatusDropdownVisible={!isIntegratedOffice}
      onCreateAppointment={onCreateAppointment}
      onUpdateAppointment={onUpdateAppointment}
      setSelectedFormValuesForAvailabilityApi={setSelectedFormValuesForAvailabilityApi}
      providersList={filteredProviderList}
      setSelectedLocationId={setSelectedLocationId}
      selectedLocationId={selectedLocationId}
      selectedLocationIds={selectedLocationIds}
      appointmentTypes={appointmentTypesData ?? []}
      refetchAppointmentTypes={refetchAppointmentTypes}
    />
  );
};
